import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dot, HelpCircle, Code, GripHorizontal } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Handle, Position, NodeProps } from 'reactflow';
import HtmlMessage from './HtmlMessage';

interface CustomNodeData {
  id: string;
  name: string;
  nodeInfo?: string;
  chatMessage?: string;
  html?: string;
  selected?: boolean;
  connecting?: boolean;
  highlighted?: boolean;
  functionCalls?: Array<{ functionName: string }>;
  displayOptions?: Array<any>;
  onSelect: (e: React.MouseEvent, id: string) => void;
  onUpdateName: (id: string, newName: string) => void;
}

const CustomNode = ({ data, dragging, dragHandle }: NodeProps<CustomNodeData>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(data.name);
  const [showHtmlDialog, setShowHtmlDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleNameSubmit = () => {
    data.onUpdateName(data.id, editedName);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setEditedName(data.name);
      setIsEditing(false);
    }
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ 
          background: '#555',
          width: '12px',
          height: '12px',
          border: '2px solid #fff',
          boxShadow: '0 0 4px rgba(0,0,0,0.2)',
          transform: 'translateX(-50%) translateY(-50%)',
          transition: 'width 0.2s, height 0.2s',
          cursor: 'crosshair',
          '&:hover': {
            width: '16px',
            height: '16px',
            background: '#666',
          }
        }}
      />
      <Card
        className={cn(
          "w-[300px] select-none",
          data.selected ? "border-primary bg-primary text-primary-foreground" : "bg-card",
          data.connecting && "border-green-500",
          data.highlighted && "ring-2 ring-yellow-400",
          dragging && "opacity-75"
        )}
        onClick={(e) => {
          console.log('Node clicked:', data.id);
          e.stopPropagation();
          if (data.onSelect) {
            data.onSelect(e, data.id);
          }
        }}
      >
        <CardHeader className="p-3 pb-0">
          <div className="flex items-center gap-2">
            <div 
              className="flex items-center gap-2 flex-1 min-w-0 p-1 hover:bg-muted/50 rounded cursor-grab active:cursor-grabbing"
              data-drag-handle
              onMouseDown={(event) => {
                // Prevent text selection during drag
                event.preventDefault();
              }}
            >
              <GripHorizontal className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              {isEditing ? (
                <Input
                  ref={inputRef}
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={handleNameSubmit}
                  onKeyDown={handleKeyDown}
                  className="h-6 flex-1"
                />
              ) : (
                <span 
                  onDoubleClick={handleDoubleClick}
                  className="truncate flex-1"
                >
                  {data.name}
                </span>
              )}
            </div>
            {data.nodeInfo && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{data.nodeInfo}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3">
          {data.chatMessage && (
            <div className="text-sm">
              <div className="line-clamp-3">
                {data.chatMessage}
              </div>
            </div>
          )}

          {data.html && (
            <div className="mt-2">
              <Dialog open={showHtmlDialog} onOpenChange={setShowHtmlDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full flex items-center gap-2"
                  >
                    <Code className="h-4 w-4" />
                    <span>View HTML Content</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>HTML Content - {data.name}</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <HtmlMessage content={data.html} />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {data.functionCalls && data.functionCalls.length > 0 && (
            <Card className="absolute -right-[120px] top-0 w-[110px]">
              <CardContent className="p-2 space-y-1">
                <div className="font-medium text-xs">Functions:</div>
                {data.functionCalls.map((call, index) => (
                  <TooltipProvider key={index}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-xs truncate cursor-help">
                          {call.functionName}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{call.functionName}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#555',
          width: '12px',
          height: '12px',
          border: '2px solid #fff',
          boxShadow: '0 0 4px rgba(0,0,0,0.2)',
          transform: 'translateX(-50%) translateY(50%)',
          transition: 'width 0.2s, height 0.2s',
          cursor: 'crosshair',
          '&:hover': {
            width: '16px',
            height: '16px',
            background: '#666',
          }
        }}
      />
    </>
  );
};

export default CustomNode;